import giftIcon from 'assets/stamp-assets/gift-icon.svg';
import tickIcon from 'assets/stamp-assets/tick-icon.png';
import cashierSelectors from 'features/cashier/services/selectors';
import { IStampLoyaltyInfo, IStampLoyaltyReward } from 'features/cashier/services/types/stamp-loyalty';
import { useEffect, useMemo, useState } from 'react';
import shopSelectors from 'services/shop/selectors';
import { formatCurrency } from 'utils/formatCurrency';
import { formatRewardLabel } from './format-reward-label';
import RewardsAvailableList from './rewards-available-list';
import { ApplyButton, BodyStyled, CancelRewardButton, CloseButton, CustomerCard, ModalFooter, ModalStyled, StampCard } from './styles';

type Props = {
  visible: boolean;
  data: IStampLoyaltyInfo;
  onClose: () => void;
  /** Apply mốc thưởng đã chọn vào ticket */
  onApply?: (reward: IStampLoyaltyReward) => void;
  /** Gỡ mốc thưởng đang áp khỏi ticket */
  onCancelReward?: () => void;
};

/** Chữ cái đầu của tối đa 2 từ đầu tiên trong tên khách, dùng cho avatar */
const getInitials = (name?: string | null) => {
  const words = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  return words.slice(0, 2).map(word => word[0]).join('').toUpperCase();
};

/** Modal chi tiết tiến độ stamp loyalty, kèm danh sách mốc thưởng khách đã mở khoá */
const DetailLoyaltyStamp = ({ visible, data, onClose, onApply, onCancelReward }: Props) => {
  const customer = cashierSelectors.getSelectedCustomer();
  const ticketDetails = cashierSelectors.getTicketDetails();
  const loyaltySetting = shopSelectors.data.getLoyaltySetting();
  const shopInfo = shopSelectors.data.shopInfo();

  const [selectedRewardId, setSelectedRewardId] = useState<string | null>(null);

  const currentStamp = Math.floor(data.totalStamp || 0);
  const stampsRequired = data.nextReward?.stampsRequired || 0;
  const remainStamp = Math.max(stampsRequired - currentStamp, 0);
  const rewards = data.rewards || [];
  // mốc đã mở khoá, dùng để bật/tắt từng dòng trong danh sách
  const unlockedIds = useMemo(
    () => new Set((data.availableRewards || []).map(reward => reward.id)),
    [data.availableRewards]
  );

  // Ticket chưa lưu lần nào thì editTicket gọi save-ticket chứ không phải update-ticket,
  // endpoint đó chưa xác nhận nhận stampRewardId nên chặn apply cho tới khi có billId.
  const hasBillId = !!ticketDetails?.ticketInfo?.billId;
  // mốc đang chọn phải còn nằm trong danh sách hiện tại, tránh apply nhầm id của lần fetch cũ
  const selectionValid = !!selectedRewardId && unlockedIds.has(selectedRewardId);
  const canApply = selectionValid && hasBillId;

  /** Mốc chọn sẵn khi mở modal: mốc đã apply trước đó, không thì mốc mở khoá đầu tiên */
  const appliedRewardId = cashierSelectors.getAppliedStampRewardId();
  const defaultRewardId = appliedRewardId || data.availableRewards?.[0]?.id || null;

  // destroyOnClose chỉ huỷ phần children của Modal, component này vẫn mounted,
  // nên phải tự đặt lại lựa chọn mỗi lần đóng hoặc mở.
  useEffect(() => {
    setSelectedRewardId(visible ? defaultRewardId : null);
  }, [visible, defaultRewardId]);

  const dollar = useMemo(() => {
    if (!loyaltySetting?.oneDollarToPoint) return 0;
    return +(customer?.point || 0) / loyaltySetting.oneDollarToPoint;
  }, [customer, loyaltySetting]);

  /** Mốc stamp nào có phần thưởng, để vẽ icon quà lên đúng ô */
  const rewardSlots = useMemo(
    () => new Set((data.rewards || []).map(reward => reward.stampsRequired)),
    [data.rewards]
  );

  const slots = useMemo(
    () => Array.from({ length: data.totalSlots || 0 }, (_, index) => index + 1),
    [data.totalSlots]
  );

  const handleApply = () => {
    if (!canApply || !selectedRewardId) return;
    const reward = rewards.find(o => o.id === selectedRewardId);
    if (reward) onApply?.(reward);
  };

  return (
    <ModalStyled
      visible={visible}
      onClose={onClose}
      modalTitle='LOYALTY STAMPS'
      width={900}
      noneBodyStyle
      destroyOnClose
      footer={
        <ModalFooter>
          <CloseButton type='button' onClick={onClose}>Close</CloseButton>
          {!!rewards.length && (appliedRewardId
            ? <CancelRewardButton type='button' onClick={onCancelReward}>Cancel Reward</CancelRewardButton>
            : <ApplyButton type='button' disabled={!canApply} onClick={handleApply}>Apply Reward</ApplyButton>
          )}
        </ModalFooter>
      }
    >
      <BodyStyled>
        <CustomerCard>
          <div className='customer-info'>
            <div className='avatar'>{getInitials(customer?.customerName)}</div>
            <div>
              <p className='customer-name'>{customer?.customerName || 'UNKNOWN CUSTOMER'}</p>
              {loyaltySetting?.active && (
                <p className='customer-point'>{customer?.point || 0} POINTS = {formatCurrency(dollar)}</p>
              )}
            </div>
          </div>
          <div className='available-stamps'>
            <p className='available-value'>{currentStamp}</p>
            <p className='available-label'>Available stamps</p>
          </div>
        </CustomerCard>

        <StampCard>
          <p className='shop-name'>{shopInfo?.shopName || ''}</p>
          <p className='card-title'>STAMP REWARDS</p>

          <div className='slot-grid'>
            {slots.map(slot => {
              const earned = slot <= currentStamp;
              const isRewardSlot = rewardSlots.has(slot);
              // ô mốc thưởng đã đạt giữ icon quà trên nền vàng, không đổi sang icon tick
              const rewardEarned = earned && isRewardSlot;
              return (
                <div className='slot' key={slot}>
                  <div className={`slot-circle ${rewardEarned ? 'reward-earned' : earned ? 'earned' : ''}`}>
                    {earned && !isRewardSlot && <img src={tickIcon} alt='' className='tick-icon' />}
                    {isRewardSlot && <img src={giftIcon} alt='' className='gift-icon' />}
                  </div>
                  <span className='slot-index'>{slot}</span>
                </div>
              );
            })}
          </div>

          {!!data.nextReward && (
            <div className='card-footer'>
              <div className='footer-col'>
                <p className='footer-label'>
                  {remainStamp > 0 ? `Only ${remainStamp} more stamps to unlock` : 'Unlocked'}
                </p>
                <p className='footer-value'>{currentStamp} of {stampsRequired} stamps</p>
              </div>
              <div className='footer-col footer-col-right'>
                <p className='footer-label'>Next Reward</p>
                <p className='footer-value'>{formatRewardLabel(data.nextReward)}</p>
              </div>
            </div>
          )}
        </StampCard>

        {!!rewards.length && (
          <RewardsAvailableList
            rewards={rewards}
            unlockedIds={unlockedIds}
            totalStamp={currentStamp}
            selectedId={selectedRewardId}
            onSelect={setSelectedRewardId}
          />
        )}
      </BodyStyled>
    </ModalStyled>
  );
};

export default DetailLoyaltyStamp;
